import SiteLayout from '@/components/feature/SiteLayout';
import PageHero from '@/components/feature/PageHero';
import CtaSection from '@/components/feature/CtaSection';
import Reveal from '@/components/base/Reveal';
import LogoSystem from '@/pages/identite/components/LogoSystem';
import ColorSystem from '@/pages/identite/components/ColorSystem';
import TypeSystem from '@/pages/identite/components/TypeSystem';
import GraphicUniverse from '@/pages/identite/components/GraphicUniverse';
import BrandSupports from '@/pages/identite/components/BrandSupports';
import usePageMeta from '@/hooks/usePageMeta';

const anchors = [
  { href: '#logo', label: 'Logo' },
  { href: '#couleurs', label: 'Palette' },
  { href: '#typographie', label: 'Typographie' },
  { href: '#univers', label: 'Univers' },
  { href: '#supports', label: 'Supports' },
];

const principles = [
  {
    icon: 'ri-anchor-line',
    title: 'Solidité',
    text: 'Un marine qui structure, des angles francs, aucune fioriture : la marque doit tenir comme un ouvrage soudé.',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Précision',
    text: 'L’orange désigne le point juste : une action, une mesure, un repère. Jamais de couleur gratuite.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Crédibilité',
    text: 'Typographie nette, photographies réelles, informations vérifiables : la même rigueur que sur un chantier.',
  },
];

export default function Identite() {
  usePageMeta(
    'Charte graphique & identité visuelle | SGSCTIC',
    'Charte graphique SGSCTIC : logo officiel et ses déclinaisons, palette bleu marine et orange industriel, typographie, univers graphique et supports professionnels.',
  );

  return (
    <SiteLayout>
      <PageHero
        breadcrumb="Charte graphique"
        label="Identité visuelle"
        title="Une marque d’ingénierie, pas une plaquette"
        description="Le socle visuel de SGSCTIC : le logo conservé à l’identique, une palette bleu marine et orange industriel, deux familles typographiques et des supports cohérents de l’atelier au chantier."
        image="https://readdy.ai/api/search-image?query=Deep%20navy%20blue%20industrial%20steel%20structure%20seen%20from%20below%2C%20geometric%20welded%20beams%20and%20pipes%2C%20cold%20blue%20light%20with%20a%20single%20warm%20orange%20glow%20in%20the%20distance%2C%20dark%20authoritative%20tones%2C%20sharp%20technical%20lines%2C%20architectural%20industrial%20photography%2C%20no%20people&width=1920&height=900&seq=sgsctic-brand-hero-navy&orientation=landscape"
        imageAlt="Structure métallique industrielle bleu marine éclairée par une lueur orange"
      />

      {/* Sommaire */}
      <div className="sticky top-[76px] z-20 border-b border-background-200 bg-background-50/95 backdrop-blur-md">
        <div className="container-wide flex items-center justify-between gap-4 overflow-x-auto py-3">
          <span className="label-mono flex-none text-foreground-400">Sommaire</span>
          <nav className="flex flex-none items-center gap-1">
            {anchors.map((anchor) => (
              <a
                key={anchor.href}
                href={anchor.href}
                className="whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
              >
                {anchor.label}
              </a>
            ))}
          </nav>
        </div>
      </div>

      {/* Principes */}
      <section className="bg-background-50 py-14 md:py-20">
        <div className="container-wide">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-16">
            <Reveal>
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-accent-500" />
                <span className="label-mono text-primary-600">Principes directeurs</span>
              </div>
              <h2 className="display-title mt-5 text-4xl text-foreground-950 md:text-5xl">
                Trois exigences,
                <br />
                tenues partout
              </h2>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-foreground-600 md:text-base">
                La charte n’ajoute pas une couche de décoration : elle rend la marque identifiable
                en trois secondes, sur un écran comme sur un panneau de chantier.
              </p>
            </Reveal>

            <div className="grid gap-px overflow-hidden rounded-lg bg-background-200">
              {principles.map((item, index) => (
                <Reveal key={item.title} delay={index * 90}>
                  <div className="flex items-start gap-4 bg-background-100 p-5 md:p-6">
                    <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
                      <i className={`${item.icon} text-lg`} />
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-heading text-lg font-semibold uppercase leading-tight text-foreground-950">
                        {item.title}
                      </h3>
                      <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
                        {item.text}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <LogoSystem />
      <ColorSystem />
      <TypeSystem />
      <GraphicUniverse />
      <BrandSupports />

      <CtaSection
        title="Vous avez un support à produire ?"
        accent="Nous cadrons le fichier."
        description="Devis, panneau de chantier, tenue, marquage de véhicule ou publication : nous appliquons la charte et préparons les fichiers aux bonnes dimensions."
      />
    </SiteLayout>
  );
}